import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../../../app.config';
import { catchErrorLogRet } from '../../../../common/rest.operators';
import { WpDisplayConfig, WpViewHeader } from '../../winning-percentage/core';
import { getFrontlineWPColumnConfigItemsConfig } from '../frontline-person/fl-winning-column-config';
import { sortByFLTotalDirectReport } from '../function/sort-by-fl-total-direct-report.function';
import { sortByFLWinning } from '../function/sort-by-fl-winning.function';
import { ColumnConfigItem, mapper } from '../model/fl-winning.global.model';
import {
    FLWinningPerson,
    FLWinningTeamTransformer,
    FLWinningTransformer,
} from '../model/fl-winning.model';
import { SortDirection } from '../model/sort-direction.model';

@Injectable()
export class FLWinningService {
    _flwp: BehaviorSubject<FLWinningPerson> =
        new BehaviorSubject<FLWinningPerson>(null);

    public _activeMember: BehaviorSubject<FLWinningPerson> =
        new BehaviorSubject<FLWinningPerson>(null);

    public _forceOpenTeam: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    public _rowExpanded: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    _flWinningSortDirection: SortDirection;
    _expectedSortDirection: SortDirection;

    // key-value for an observable and primary key of metrics
    // metric icon state observables
    private _flWinningColumnConfig: BehaviorSubject<ColumnConfigItem[]>;

    private readonly _DataRangeID: BehaviorSubject<number> =
        new BehaviorSubject<number>(null);

    private readonly _EvalRangeID: BehaviorSubject<number> =
        new BehaviorSubject<number>(null);

    private readonly _metricColumnConfig: BehaviorSubject<ColumnConfigItem[]>;

    // current state of the Frontline Winning component section
    private _cachedFlwinning: any;

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _translateService: TranslateService,
    ) {}

    public hasPendingRefresh: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);

    resetWidget(): void {
        this._flwp.next(null);
        this._activeMember.next(null);
        this._flWinningColumnConfig.next(null);
    }

    get flwp(): Observable<FLWinningPerson> {
        return this._flwp.asObservable();
    }

    get flWinningColumns(): Observable<ColumnConfigItem[]> {
        return this._flWinningColumnConfig.asObservable();
    }

    get expected(): number {
        return 10;
    }

    get rowExpanded(): Observable<boolean> {
        return this._rowExpanded.asObservable();
    }

    get activeMember(): Observable<FLWinningPerson> {
        return this._activeMember.asObservable();
    }

    get forceOpenTeam(): Observable<boolean> {
        return this._forceOpenTeam.asObservable();
    }

    getDirection(type: string): SortDirection {
        return type === 'oaSort'
            ? this._flWinningSortDirection
            : this._expectedSortDirection;
    }

    setRowExpanded(val: boolean): void {
        this._rowExpanded.next(val);
    }

    getDataRangeID(): number {
        return this._DataRangeID.getValue();
    }

    getEvalRangeID(): number {
        return this._EvalRangeID.getValue();
    }

    setDataRangeID(val: number): void {
        this._DataRangeID.next(val);
    }

    setEvalRangeID(val: number): void {
        this._EvalRangeID.next(val);
    }

    getColumns(): ColumnConfigItem[] {
        return this._flWinningColumnConfig.getValue();
    }

    retrieveFLWinning(
        userId: number,
        dataRangeId?: number,
        evaluationRangeId?: number,
    ): Observable<FLWinningPerson> {
        let params = new HttpParams().set('userId', String(userId));
        if (dataRangeId != null) {
            params = params.set('dataRangeId', String(dataRangeId));
        }
        if (evaluationRangeId != null) {
            params = params.set('evaluationRangeId', String(evaluationRangeId));
        }
        const req1 = this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/winning-percentage/fl-wp-parent`,
                {
                    params,
                },
            )
            .pipe(map(mapper));

        const sub = req1.subscribe(
            (data: FLWinningPerson) => {
                sub.unsubscribe();
                this._mapResponse(data);
            },
            (error: Error) => {
                sub.unsubscribe();
                this._mapResponse(error);
            },
        );
        return req1;
    }

    retrieveTeamWinning(
        userId: number,
        dataRangeId?: number,
        evaluationRangeId?: number,
    ): Observable<FLWinningPerson[]> {
        let params = new HttpParams().set('userId', String(userId));
        if (dataRangeId != null) {
            params = params.set('dataRangeId', String(dataRangeId));
        }
        if (evaluationRangeId != null) {
            params = params.set('evaluationRangeId', String(evaluationRangeId));
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/winning-percentage/fl-wp-child`,
                {
                    params,
                },
            )
            .pipe(
                map(mapper),
                map((data) => this._mapTeamResponse(data)),
            );
    }

    setForceOpenTeam(state: boolean): void {
        this._forceOpenTeam.next(state);
    }

    setCurrentFlWinningState(f: FLWinningPerson): void {
        this._flwp.next(f);
    }

    private _mapResponse_(data: any, mapToGlobalServiceData = true): void {
        const flWp = new FLWinningTransformer().transform(data);
        if (flWp.userId == null) {
            this._flwp.next(null);
            return;
        }

        if (mapToGlobalServiceData) {
            this._flwp.next(flWp);
        }
    }

    private _mapResponse(
        data: any,
        mapToGlobalServiceData = true,
    ): Observable<FLWinningPerson | null> {
        return new Observable<FLWinningPerson | null>((sub) => {
            const flWp = new FLWinningTransformer().transform(data);

            if (flWp.userId == null) {
                this._flwp.next(null);
                sub.next(null);
                sub.complete();
            } else {
                if (mapToGlobalServiceData) {
                    this._flwp.next(flWp);
                }
                sub.next(flWp);
                sub.complete();
            }
        });
    }

    private _mapTeamResponse(data: any): FLWinningPerson[] {
        const leader: FLWinningPerson = this._flwp.getValue(); // there is only 1 leader per winning view
        const flWinning = new FLWinningTeamTransformer(leader).transform(data);

        // use the current sort, but don't change direction
        if (
            (this._flWinningSortDirection === SortDirection.DEFAULT &&
                this._expectedSortDirection === SortDirection.DEFAULT) ||
            (this._expectedSortDirection !== SortDirection.DEFAULT &&
                this._flWinningSortDirection === SortDirection.DEFAULT)
        ) {
            flWinning.sort(
                sortByFLTotalDirectReport(this._expectedSortDirection),
            );
        } else if (
            this._flWinningSortDirection !== SortDirection.DEFAULT &&
            this._expectedSortDirection === SortDirection.DEFAULT
        ) {
            flWinning.sort(sortByFLWinning(this._flWinningSortDirection));
        }

        return flWinning;
    }

    initializeColumnConfig(): void {
        this._flWinningColumnConfig = new BehaviorSubject<ColumnConfigItem[]>(
            getFrontlineWPColumnConfigItemsConfig(this._translateService),
        );
        this._flWinningSortDirection = SortDirection.DESC;
        this._expectedSortDirection = SortDirection.DEFAULT;
    }

    traverse(
        type: string,
        dir: SortDirection,
        subordinates: FLWinningPerson[],
    ): void {
        if (subordinates.length === 0) return;

        const expandedsubs = subordinates.filter(
            (f) =>
                (f.isExpanded ?? false) &&
                f.subOrdinates &&
                f.subOrdinates.length,
        );
        if (expandedsubs.length > 0) {
            expandedsubs.forEach((q) => {
                this.traverse(type, dir, q.subOrdinates);
            });
        } else {
            if (type === 'oascore') {
                subordinates.sort(sortByFLWinning(dir)); // default Sort for all
            } else if (type === 'expected') {
                if (dir === SortDirection.DEFAULT) {
                    subordinates.sort(sortByFLTotalDirectReport(dir)); // default Sort for all
                } else {
                    subordinates.sort(sortByFLTotalDirectReport(dir));
                }
            }
        }
    }

    sortExpected(forceDirection?: SortDirection): SortDirection {
        if (this._expectedSortDirection === SortDirection.DESC) {
            this._expectedSortDirection = SortDirection.ASC;
            this._flWinningSortDirection = SortDirection.DEFAULT;
        } else if (this._expectedSortDirection === SortDirection.ASC) {
            this._expectedSortDirection = SortDirection.DEFAULT;
            this._flWinningSortDirection = SortDirection.DESC;
        } else if (this._expectedSortDirection === SortDirection.DEFAULT) {
            this._expectedSortDirection = SortDirection.DESC;
            this._flWinningSortDirection = SortDirection.DEFAULT;
        }

        if (forceDirection != null) {
            this._expectedSortDirection = forceDirection;
        }

        return this._expectedSortDirection;
    }

    sortFLWP(forceDirection?: SortDirection): SortDirection {
        this._flWinningSortDirection =
            this._flWinningSortDirection === SortDirection.DESC
                ? SortDirection.ASC
                : SortDirection.DESC;

        this._expectedSortDirection = SortDirection.DEFAULT;

        if (forceDirection != null) {
            this._flWinningSortDirection = forceDirection;
        }

        return this._flWinningSortDirection;
    }

    sortTeamBy(
        type: string,
        force?: SortDirection,
        flWinning?: FLWinningPerson,
    ): SortDirection {
        if (flWinning == null) {
            flWinning = this._flwp.getValue();
        }

        const dir =
            type === 'oascore'
                ? this.sortFLWP(force)
                : this.sortExpected(force);

        this.traverse(type, dir, flWinning.subOrdinates);

        return dir;
    }

    saveCurrentViewState(): void {
        const floa = this._flwp.getValue();
        this._cachedFlwinning = JSON.stringify(floa);
    }

    retrieveDisplayConfig(): Observable<WpDisplayConfig> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/winning-percentage/display-config`,
            )
            .pipe(catchErrorLogRet({}));
    }

    retrieveViewHeader(): Observable<WpViewHeader> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/winning-percentage/header`,
            )
            .pipe(catchErrorLogRet({}));
    }
}
